import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { persistStorage } from '@/stores/persistStorage';
import type { WeekStart } from '@/lib/week';

export type Theme = 'light' | 'dark' | 'system';
export type TextSize = 'compact' | 'default' | 'large';
export type SuggestionStyle = 'focused' | 'balanced' | 'exploratory';
export type NotificationKey = 'notifTasks' | 'notifHabits' | 'notifRoutines' | 'notifCalendar';
export type GoogleSyncBroken = 'token-expired' | 'not-connected';

export interface UIState {
  theme: Theme;
  sidebarCollapsed: boolean;
  hourHeightPx: number;
  lastViewedDate: Date;
  googleAccessToken?: string | undefined;
  googleAccessTokenExpiresAt?: Date | undefined;
  googleAccountEmail?: string | undefined;
  // Set when the server says the stored refresh token is dead ('token-expired')
  // or missing ('not-connected'). Persisted so the Reconnect prompt survives
  // restarts — without it the UI reads the mere presence of googleAccessToken
  // as "Connected ✓" forever while every sync silently fails.
  googleSyncBroken?: GoogleSyncBroken | undefined;
  // Provider the user last signed in with — persisted so the Account label
  // survives a cold start. The restored JS user's providerData[0] is unreliable
  // on accounts with BOTH Google and Apple linked (it reflects link order, not
  // the session's actual provider), so we remember the real one here.
  lastAuthProvider: 'google' | 'apple' | null;
  setLastAuthProvider: (provider: 'google' | 'apple' | null) => void;
  // Phase 12 — Settings preferences
  weekStart: WeekStart;
  setWeekStart: (start: WeekStart) => void;
  textSize: TextSize;
  suggestionStyle: SuggestionStyle;
  microStepEnabled: boolean;
  notifTasks: boolean;
  notifHabits: boolean;
  notifRoutines: boolean;
  notifCalendar: boolean;
  quietHoursStart: string; // "HH:MM" 24h
  quietHoursEnd: string; // "HH:MM" 24h
  scheduleStyle: 'structured' | 'flexible' | 'ai-driven' | null;
  energyPattern: {
    peakStart: number;
    peakEnd: number;
    creativeStart: number;
    creativeEnd: number;
    winddownStart: number;
    winddownEnd: number;
  } | null;
  setScheduleStyle: (style: UIState['scheduleStyle']) => void;
  setEnergyPattern: (pattern: NonNullable<UIState['energyPattern']>) => void;
  dismissedInsights: Record<string, number>;
  dismissInsight: (insightId: string) => void;
  // Transient overlay state — NOT persisted.
  commandPaletteOpen: boolean;
  /** Draft text handed from the capture composer's "Ask instead" — consumed
   *  once by AskSheet on open, then cleared. Ephemeral. */
  askSeed: string | null;
  /** Set by the /e/pro deep link — consumed once by SubscriptionSection on
   *  the Settings page, which opens the upgrade sheet and clears it. Ephemeral. */
  upgradeSheetRequested: boolean;
  shortcutsHelpOpen: boolean;
  /** Native keyboard is up (Capacitor keyboardWillShow/Hide). Ephemeral. */
  keyboardVisible: boolean;
  compressionDialogRoutineId: string | null;
  openCompressionDialog: (routineId: string) => void;
  closeCompressionDialog: () => void;
  setTheme: (theme: Theme) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setHourHeight: (px: number) => void;
  setLastViewedDate: (date: Date) => void;
  setGoogleAccessToken: (token: string, expiresAt: Date, email?: string) => void;
  clearGoogleAccessToken: () => void;
  setGoogleSyncBroken: (reason: GoogleSyncBroken | undefined) => void;
  setTextSize: (size: TextSize) => void;
  setSuggestionStyle: (style: SuggestionStyle) => void;
  setMicroStepEnabled: (enabled: boolean) => void;
  setNotificationToggle: (key: NotificationKey, enabled: boolean) => void;
  setQuietHours: (start: string, end: string) => void;
  toggleCommandPalette: () => void;
  setCommandPaletteOpen: (open: boolean) => void;
  setAskSeed: (seed: string | null) => void;
  setUpgradeSheetRequested: (requested: boolean) => void;
  setShortcutsHelpOpen: (open: boolean) => void;
  setKeyboardVisible: (visible: boolean) => void;
}

const MIN_HOUR_HEIGHT = 50;
const MAX_HOUR_HEIGHT = 140;
const DEFAULT_HOUR_HEIGHT = 80;

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      theme: 'system',
      lastAuthProvider: null,
      weekStart: 1,
      textSize: 'default',
      suggestionStyle: 'balanced',
      microStepEnabled: false,
      notifTasks: true,
      notifHabits: true,
      notifRoutines: true,
      notifCalendar: true,
      quietHoursStart: '22:00',
      quietHoursEnd: '07:00',
      scheduleStyle: null,
      energyPattern: null,
      setScheduleStyle: (scheduleStyle) => set({ scheduleStyle }),
      setEnergyPattern: (energyPattern) => set({ energyPattern }),
      sidebarCollapsed: false,
      hourHeightPx: DEFAULT_HOUR_HEIGHT,
      lastViewedDate: new Date(),
      commandPaletteOpen: false,
      askSeed: null,
      upgradeSheetRequested: false,
      shortcutsHelpOpen: false,
      keyboardVisible: false,
      compressionDialogRoutineId: null,
      dismissedInsights: {},
      setTheme: (theme) => set({ theme }),
      setLastAuthProvider: (lastAuthProvider) => set({ lastAuthProvider }),
      setWeekStart: (weekStart) => set({ weekStart }),
      setTextSize: (textSize) => set({ textSize }),
      setSuggestionStyle: (suggestionStyle) => set({ suggestionStyle }),
      setMicroStepEnabled: (microStepEnabled) => set({ microStepEnabled }),
      setNotificationToggle: (key, enabled) => set(() => ({ [key]: enabled })),
      setQuietHours: (quietHoursStart, quietHoursEnd) => set({ quietHoursStart, quietHoursEnd }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setHourHeight: (px) =>
        set({ hourHeightPx: Math.min(MAX_HOUR_HEIGHT, Math.max(MIN_HOUR_HEIGHT, px)) }),
      setLastViewedDate: (date) => set({ lastViewedDate: date }),
      setGoogleAccessToken: (token, expiresAt, email) =>
        set({
          googleAccessToken: token,
          googleAccessTokenExpiresAt: expiresAt,
          googleAccountEmail: email,
          googleSyncBroken: undefined,
        }),
      clearGoogleAccessToken: () =>
        set({
          googleAccessToken: undefined,
          googleAccessTokenExpiresAt: undefined,
          googleAccountEmail: undefined,
          googleSyncBroken: undefined,
        }),
      setGoogleSyncBroken: (reason) => set({ googleSyncBroken: reason }),
      toggleCommandPalette: () => set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),
      setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),
      setAskSeed: (askSeed) => set({ askSeed }),
      setUpgradeSheetRequested: (upgradeSheetRequested) => set({ upgradeSheetRequested }),
      setShortcutsHelpOpen: (open) => set({ shortcutsHelpOpen: open }),
      setKeyboardVisible: (keyboardVisible) => set({ keyboardVisible }),
      openCompressionDialog: (routineId) => set({ compressionDialogRoutineId: routineId }),
      closeCompressionDialog: () => set({ compressionDialogRoutineId: null }),
      dismissInsight: (insightId) =>
        set((s) => ({
          dismissedInsights: { ...s.dismissedInsights, [insightId]: Date.now() },
        })),
    }),
    {
      name: 'brainday:ui',
      storage: persistStorage<UIState>(),
      partialize: (state) =>
        ({
          theme: state.theme,
          sidebarCollapsed: state.sidebarCollapsed,
          hourHeightPx: state.hourHeightPx,
          lastViewedDate: state.lastViewedDate,
          googleAccessToken: state.googleAccessToken,
          googleAccessTokenExpiresAt: state.googleAccessTokenExpiresAt,
          googleAccountEmail: state.googleAccountEmail,
          googleSyncBroken: state.googleSyncBroken,
          dismissedInsights: state.dismissedInsights,
          lastAuthProvider: state.lastAuthProvider,
          weekStart: state.weekStart,
          textSize: state.textSize,
          suggestionStyle: state.suggestionStyle,
          microStepEnabled: state.microStepEnabled,
          notifTasks: state.notifTasks,
          notifHabits: state.notifHabits,
          notifRoutines: state.notifRoutines,
          notifCalendar: state.notifCalendar,
          quietHoursStart: state.quietHoursStart,
          quietHoursEnd: state.quietHoursEnd,
          scheduleStyle: state.scheduleStyle,
          energyPattern: state.energyPattern,
        }) as UIState,
    },
  ),
);
